import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Card from "../Generic/Card";
import Dropdown from "../Generic/Dropdown";
import FoodMakingService from "./Services/FoodMakingService";
import IotService from "./Services/iotService";
import { LoadingToast, UpdateToastFailed, UpdateToastSuccess, errorNotification, successNotification } from "../Generic/Notifications";
import Button from "../Generic/Button";
import { bindActionCreators } from "redux";
import { setMachineID } from "../Landing/Actions";
import * as Actions from "./Actions";
import { connect } from "react-redux";
import Loader from "../Generic/Loader";
import { isEmpty } from "../util/util";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: left;
	padding: 2rem;
	gap:1rem;
	@media (max-width: 1000px) {
		padding: 2rem;
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 26px;
	margin: 0 0 0 0.5rem;
`;

const SpacedOutRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	width: 100%;
	height: max-content;
`;

const Row = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
	gap: ${(props) => props.gap};
`;

const PrimaryText = styled.div`
	color: rgba(0, 0, 0, 1);
	font-size: 18px;
	font-weight: 600;
	margin: ${(props) => props.margin};
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.1);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 310;
`;

const CenteredContainer = styled.div`
	width: 100%;
	height: max-content;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: ${(props) => props.margin};
`;

const TASKS = {
	"PRIME_PREPARE" : "prime_prepare"
}

const COMPONENTS = {
	PAGE: "PAGE",
	RESTAURANT_STATUS: "RESTAURANT_STATUS",
	RESTAURANT_UPDATE: "RESTAURANT_UPDATE",
};

const COMPONENT_STATES = {
	LOADING: "LOADING",
	ERROR: "ERROR",
	OK: "OK",
};

const INITIAL_COMPONENT_STATES = {
	[COMPONENTS.PAGE]: COMPONENT_STATES.OK,
	[COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.LOADING,
	[COMPONENTS.RESTAURANT_UPDATE]: COMPONENT_STATES.OK,
};

const FoodMaking = (props) => {
	//States and Functions of Redux declared for use
	const { machineID, kitchenId, restaurant, setRestaurantData, RestaurantServingStatusesMap, RestaurantServingStatuses } = props;
	const [restoStateToSet, setRestoStateToSet] = useState(null);
	const [componentStates, changeComponentStates] = useState(INITIAL_COMPONENT_STATES);
	const restaurantStatusOptions = RestaurantServingStatuses ? Object.entries(RestaurantServingStatuses).map((item) => ({
		head: item[0],
		value: item[1],
	})): [];

	async function updateResturantStatus() {
		if(restoStateToSet === null) {
			errorNotification("No restaurant status selected");
			return;
		}
		changeComponentStates((prev) => ({ ...prev, [COMPONENTS.PAGE]: COMPONENT_STATES.LOADING }));
		const { data, error } = await FoodMakingService.changeRestaurantServingStatus(machineID, { servingStatus: restoStateToSet });
		changeComponentStates((prev) => ({ ...prev, [COMPONENTS.PAGE]: COMPONENT_STATES.OK }));
		if (data) {
			const { erroredRestaurants } = data || {};
			if(isEmpty(erroredRestaurants)) {
				successNotification(`Restaurant Status set to ${RestaurantServingStatusesMap[restoStateToSet]}`);
				setRestoStateToSet(null);
				getRestaurantStatus(true);
			} else {
				const { activeOrders } = erroredRestaurants?.[machineID] || {}
				if(activeOrders) {
					errorNotification(`Could not change Restaurant Status to ${RestaurantServingStatusesMap[restoStateToSet]} as the Restaurant has Active Orders`);
				} else {
					errorNotification(`Could not change Restaurant Status to ${RestaurantServingStatusesMap[restoStateToSet]}`);
				}
			}
		}
		if (error) {
			errorNotification(error);
		}
	}

	async function sendPrimePrepareTask() {
		const PrimePrepareTask = "prime_prepare";
		const toastId = LoadingToast(`Sending Prime Prepare Task`);
		const { data, error } = await IotService.sendTask(kitchenId, machineID, PrimePrepareTask);
		if(data) {
			UpdateToastSuccess('Prime Prepare task sent', toastId);
		}
		if(error) {
			UpdateToastFailed('Prime Prepare task failed to send', toastId);
		}
	}

	async function getRestaurantStatus(handleLoading = false) {
		if(handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.LOADING }));
		const { data, error } = await FoodMakingService.getRestaurantData(machineID);
		if(handleLoading) changeComponentStates((prev) => ({ ...prev, [COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.OK }));
		if (data) {
			setRestaurantData({ ...data })
		}
		if (error) {
			changeComponentStates((prev) => ({ ...prev, [COMPONENTS.RESTAURANT_STATUS]: COMPONENT_STATES.ERROR }));
			errorNotification(error);
		}
	}

	useEffect(() => {
		getRestaurantStatus(true);
	}, []);

	function renderRestaurantStatus(data, componentState) {
		let renderData = [];

		switch (componentState) {
			case COMPONENT_STATES.LOADING:
				renderData.push(
					<CenteredContainer>
						<Loader />
					</CenteredContainer>
				);
				break;

			case COMPONENT_STATES.ERROR:
				renderData.push(<CenteredContainer>Failed to Restaurant Status</CenteredContainer>);
				break;

			case COMPONENT_STATES.OK:

				const servingStatus = RestaurantServingStatusesMap?.[data?.servingStatus] || "-";

				renderData.push(
					<Row gap="1rem 2rem">
						<Row gap="1rem">
							<PrimaryText>Serving Status</PrimaryText>- <PrimaryText>{servingStatus}</PrimaryText>
						</Row>
					</Row>
				);
				break;

			default:
				break;
		}

		return (
			<Card jContent="" alignItems="" padding="1.5rem" gap="2rem" width="max-content" margin="0 0 1rem 0">
				{renderData}
			</Card>
		);
	}

	return (
		<Page>
			{componentStates[COMPONENTS.PAGE] === COMPONENT_STATES.LOADING ? <Overlay /> : ""}
			<Header>Restaurant Data</Header>
			{renderRestaurantStatus(restaurant, componentStates[COMPONENTS.RESTAURANT_STATUS])}

			<Header>Actions</Header>
			<Card
				gap="1rem"
				padding="1rem"
				jContent="center"
				width="90%"
				widthAfter1000="max-content"
				flexWrap="wrap"
				flexDirection="row">
					<Div>
						<Dropdown
							height="100%"
							widthVal="16rem"
							head="Set Serving State"
							optionsList={restaurantStatusOptions}
							value={restoStateToSet}
							onChange={setRestoStateToSet}
						/>
						&nbsp;
						<Button heigh onClick={updateResturantStatus}>Set</Button>
					</Div>

					<Button onClick={sendPrimePrepareTask}>Prime Prepare</Button>
			</Card>
		</Page>
	);
};

const mapStatetoProps = (state) => {
	const { foodmaking, landing } = state;
	return {
		machineID: landing.machineID,
		kitchenId: landing.kitchenId,
		...foodmaking,
	};
};

const mapDispatchToProps = (dispatch) => ({
	...bindActionCreators(
		{
			setMachineID: setMachineID,
			...Actions,
		},
		dispatch
	),
});

export default connect(mapStatetoProps, mapDispatchToProps)(FoodMaking);
